import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps, useModal } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useCompanyUsers, useDeleteCompanyUser } from '@/services/api/hooks/companies';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import SiPlus from '@/icons/SiPlus';
import { User } from '@/services/api/commonTypes';
import { FunctionComponent, Suspense } from 'react';
import InviteUsersToCustomerModal from './InviteUsersToCustomerModal';

const ManageCustomerUsersModal: FunctionComponent<ModalProps<{ mspId: string; customerId: string; asCustomer: boolean }>> = props => {
  const asCustomer = props.props?.asCustomer;
  const ManageCustomerUsers = () => {
    const mspId = props.props?.mspId;
    const customerId = props.props?.customerId;
    
    if (!mspId) throw new Error('Missing partner id');
    if (!customerId) throw new Error('Missing company id');

    const [users] = useCompanyUsers(mspId!, customerId!);

    const deleteUser = useDeleteCompanyUser();

    const onDelete = async (id: string) => {
      await deleteUser.mutateAsync({ partnerId: mspId, companyId: customerId, id });
    };

    const errorObj = deleteUser.error as Error | undefined;
    const error: string = errorObj ? errorObj.message : '';

    const onCustomerInvite = () => inviteCustomerModalProps.open({ mspId, customerId });

    const inviteCustomerModalProps = useModal<{ mspId: string; customerId: string }>();

    return (
      <>
        <InviteUsersToCustomerModal {...inviteCustomerModalProps} />
        <div className="mt-5 space-y-4">
          <Button onClick={onCustomerInvite} className="ml-auto">
            <SiPlus className="text-xl" /><span>{asCustomer ? "Customer Authorised Agents" : "Customer"}</span>
          </Button>

          {users.items.map((user: User) => (
            <div className="flex justify-between space-x-3 items-center" key={user.id}>
              <div>
                <p key={user.id} className="font-medium">
                  {user.givenName} {user.surname}
                </p>

                <p>{user.email}</p>
              </div>

              {error && <p className="text-red-500">{error}</p>}

              <Button
                className="h-fit"
                onClick={async () => {
                  await onDelete(user.id);
                }}
                loading={deleteUser.isLoading}
                variant={ButtonVariant.Destructive}
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">{asCustomer ? "Authorised Agents" : "Invite Customer"}</h1>
        </Modal.Controls>

        <Suspense fallback={<Loading />}>
          <ManageCustomerUsers />
        </Suspense>
      </Card>
    </Modal>
  );
};

export default ManageCustomerUsersModal;
